import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { SESSION_QUERY_KEY, useApiClient, useSession } from '@platform/api-client/react';
import { ImpersonationBanner, useToast } from '@platform/ui';

import { describeApiError } from './describe-error';
import { isTrustedRedirect, redirectUrlOf } from './next-path';

/**
 * The impersonation banner, on the clinic side (M2.7, M2.8, ADR-005, SECURITY.md §6.3,
 * FR-SUPER-06, rules §20.5, §20.6).
 *
 * **This app is where a support session actually happens.** The super admin console has
 * no clinic screens, so `POST /platform/impersonation` answers with a `redirectUrl` into
 * the tenant's admin host and the operator lands *here*, holding the target staff
 * member's permissions. The banner is the promise rules §20.5 requires: it is above the
 * top bar, on every screen, for the whole session.
 *
 * Nothing is held in React state. The banner is drawn from `session.impersonation`,
 * which the API re-reads from the `impersonation_sessions` row on **every** request -
 * so a support session that is revoked or that times out server-side stops showing a
 * banner on the next session read, rather than leaving a stale one on screen claiming an
 * access that is gone.
 */

export interface ImpersonationValue {
  /** The banner payload, or null when this session is not impersonating. */
  session: {
    impersonatorEmail: string;
    startedAt: string;
    expiresAt: string;
    blockedPermissions: readonly string[];
  } | null;
  /** Who is being viewed as, when the session knows the name. */
  targetName: string | null;
  tenantName: string | null;
}

/** Everything the banner needs, straight from `GET /auth/session`. */
export function useImpersonation(): ImpersonationValue {
  const session = useSession();
  const data = session.data;
  const impersonation = data?.impersonation ?? null;

  if (impersonation === null) return { session: null, targetName: null, tenantName: null };

  return {
    session: {
      impersonatorEmail: impersonation.impersonatorEmail,
      startedAt: impersonation.startedAt,
      expiresAt: impersonation.expiresAt,
      blockedPermissions: impersonation.blockedPermissions,
    },
    // Inside a support session `user` is the *target* - that is the whole point of the
    // derived session - so the banner's "viewing as" comes from there.
    targetName: data?.user?.name ?? data?.user?.email ?? null,
    tenantName: data?.tenant?.name ?? null,
  };
}

export interface ImpersonationBannerConnectedProps {
  /** Injected by tests so the countdown is deterministic. */
  now?: () => number;
  /** The page's own location, so a test can assert the redirect without navigating. */
  location?: { protocol: string; host: string; assign: (url: string) => void };
}

/**
 * Exit is `DELETE /platform/impersonation/current`, then a session refresh.
 *
 * That endpoint is deliberately unpermissioned and MFA-exempt on the server, because
 * inside a support session the caller holds the *target's* permissions - leaving must
 * never be harder than entering (rules §20.6, ADR-005). It is therefore also the one
 * action in this app that must work when everything else is refused, which is why the
 * session is re-read whether the call succeeded or failed: if the record had already
 * closed server-side the call answers `404`, and the right response to that is still
 * "re-read the session".
 *
 * **Where the operator goes next.** The endpoint answers `204` with no body today, so
 * there is nothing to follow: the derived cookie is gone, the refreshed session is
 * anonymous, and `RequireAuth` sends them to this clinic's `/login`. If the API grows a
 * `redirectUrl` back to the platform console (`Docs/ADMIN_AUTH_UI.md` §8, open item 1)
 * it is followed here - but only after `isTrustedRedirect`, because "the server told us
 * to" is how open redirects get shipped.
 */
export function ImpersonationBannerConnected({
  now,
  location,
}: ImpersonationBannerConnectedProps = {}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session, targetName, tenantName } = useImpersonation();
  const [exiting, setExiting] = useState(false);

  const exit = useCallback((): void => {
    setExiting(true);
    void (async () => {
      let redirect: string | null = null;
      try {
        redirect = redirectUrlOf(await api.auth.impersonationStop());
      } catch (caught) {
        toast({ tone: 'error', message: describeApiError(caught) });
      }

      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' });
      setExiting(false);

      if (redirect === null) return;
      const here = location ?? window.location;
      if (isTrustedRedirect(redirect, here)) {
        here.assign(redirect);
      } else {
        toast({
          tone: 'warning',
          message:
            'The support session ended, but the address we were sent back to was not one we recognise.',
        });
      }
    })();
  }, [api, queryClient, toast, location]);

  if (session === null) return null;

  return (
    <ImpersonationBanner
      impersonatorEmail={session.impersonatorEmail}
      expiresAt={session.expiresAt}
      blockedPermissions={session.blockedPermissions}
      {...(targetName === null ? {} : { targetLabel: targetName })}
      {...(tenantName === null ? {} : { tenantName })}
      {...(now === undefined ? {} : { now })}
      exiting={exiting}
      onExit={exit}
    />
  );
}
